import React, { useState, useEffect } from 'react';

import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import AddIcon from '@material-ui/icons/Add';
import { useTheme } from '@material-ui/core/styles';

import InputLabel from '../../../inputfields/InputLabel';
import ListDropDown from '../../../inputfields/ListDropDown';
import AiFieldVerdict from '../AiFieldVerdict';
import IProduct, { IOtherSubstance } from '../../../../models/product';
import { SUPPLEMENT_SUBSTANCE_UNITS } from '../../../constants-ts';
import useViewport from 'hooks/useViewports';

interface IOtherSubstancesProps {
  readOnly?: boolean;
  state: IProduct;
  setState: (state: IProduct) => void;
}

const AmountField = ({
  readOnly,
  value,
  onChange,
  smUp,
}: {
  readOnly?: boolean;
  value?: number;
  onChange: (val?: number) => void;
  smUp: boolean;
}) => {
  const [localValue, setLocalValue] = useState<string>(value != null ? String(value) : '');

  useEffect(() => {
    if (value == null) {
      if (localValue !== '') setLocalValue('');
      return;
    }
    const parsedLocal = parseFloat(localValue.replace(',', '.'));
    if (parsedLocal === value) return;

    setLocalValue(String(value));
  }, [value, localValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const clean = raw.trim() ? raw.replace(/[^0-9.,]/g, '') : '';
    setLocalValue(clean);

    if (clean === '') {
      onChange(undefined);
      return;
    }
    const parsed = parseFloat(clean.replace(',', '.'));
    onChange(Number.isFinite(parsed) ? parsed : undefined);
  };

  return (
    <TextField
      disabled={readOnly}
      label="Mängd per dagsdos"
      placeholder="t.ex. 250"
      variant="outlined"
      margin="normal"
      value={localValue}
      onChange={handleChange}
      style={{ flex: smUp ? 1 : undefined, marginTop: 5 }}
      fullWidth={!smUp}
      inputProps={{ inputMode: 'decimal' }}
    />
  );
};

export const OtherSubstances = ({ readOnly, state, setState }: IOtherSubstancesProps) => {
  const { smUp } = useViewport();
  const theme = useTheme();

  const items: IOtherSubstance[] = state.other_substances || [];

  const updateItem = (index: number, patch: Partial<IOtherSubstance>) => {
    const next = items.map((item, i) => (i === index ? { ...item, ...patch } : item));
    setState({ ...state, other_substances: next });
  };

  const removeItem = (index: number) => {
    const next = items.filter((_, i) => i !== index);
    setState({ ...state, other_substances: next });
  };

  const addItem = () => {
    setState({
      ...state,
      other_substances: [...items, { name: '', unit: '' }],
    });
  };

  return (
    <>
      <InputLabel
        heading="Övriga ämnen/växtextrakt"
        text="Lägg till icke-standardiserade ämnen som extrakt, bakteriekulturer m.m. som inte ingår i vitamin- och mineralpanelen ovan."
      />

      {items.length > 0 ? (
        items.map((item, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              flexDirection: smUp ? 'row' : 'column',
              alignItems: smUp ? 'flex-start' : 'stretch',
              gap: 8,
              marginBottom: 8,
            }}
          >
            <TextField
              disabled={readOnly}
              label="Ämne/växtextrakt"
              placeholder="t.ex. Echinacea-extrakt"
              variant="outlined"
              margin="normal"
              value={item.name || ''}
              onChange={e => updateItem(index, { name: e.target.value })}
              style={{ flex: smUp ? 2 : undefined, marginTop: 5 }}
              fullWidth={!smUp}
            />
            <AmountField
              readOnly={readOnly}
              value={item.amount}
              onChange={amount => updateItem(index, { amount })}
              smUp={smUp}
            />
            <ListDropDown
              readOnly={readOnly}
              label="Enhet"
              listData={SUPPLEMENT_SUBSTANCE_UNITS}
              handleChange={e => updateItem(index, { unit: String(e.target.value || '') })}
              value={item.unit ?? ''}
              targetName={`other_substances[${index}].unit`}
              style={{ flex: smUp ? 1 : undefined, marginTop: 5 }}
            />
            {!readOnly && (
              <IconButton
                style={{ color: theme.palette.error.main, alignSelf: smUp ? 'center' : 'flex-end' }}
                onClick={e => {
                  e.preventDefault();
                  removeItem(index);
                }}
                aria-label="Ta bort rad"
              >
                <RemoveCircleIcon fontSize="large" />
              </IconButton>
            )}
          </div>
        ))
      ) : readOnly ? (
        <div
          style={{
            fontSize: 13,
            color: '#9e9e9e',
            fontStyle: 'italic',
            padding: '8px 0',
          }}
        >
          Inga ämnen tillagda än.
        </div>
      ) : null}

      {!readOnly && (
        <Button onClick={addItem} variant="contained" color="primary" startIcon={<AddIcon />}>
          Lägg till ämne
        </Button>
      )}
      {/* The rows have no field of their own named like the review, so the verdict goes under the whole list. */}
      <AiFieldVerdict field="other_substances" />
    </>
  );
};

export default OtherSubstances;
